import { useQuery } from '@tanstack/react-query';
import { RouteById } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { PencilIcon } from 'lucide-react';

import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';

import { EmptyArray, getStudentFeedbacks } from '@/lib/api-calls.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { RegisteredRouteTree, RoutesWithValidConstraint, useNavigateSearchState } from '@/lib/routing.js';

import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { useSorting } from '@/lib/sorting.js';
import { useTimeZone } from '@/auth/useTimeZone.js';
import {
  getStudentFeedbackIssueOptions,
  getStudentFeedbackStatusOptions,
  StudentFeedbacksTableRouteConstraint,
} from '@/lib/student-feedback-utils.js';
import { airColumnHelper } from '@/lib/tables.js';

import { StudentFeedbackBulkUpdate } from '@/components/feedbacks/student-feedback-bulk-update.js';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker.js';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list.js';
import { formatInstant, FrequentTimeZones, plainDateFromStringOrElseUndefined } from '@airreading/common';
import {
  GetStudentFeedbacksSortableColumn,
  getStudentFeedbacksSortableColumnSchema,
  StudentFeedback,
  StudentFeedbackIssueLabelMap,
  StudentFeedbackStatus,
  StudentFeedbackStatusLabelMap,
} from '@airreading/common/data';
import { EditStudentFeedbackModal } from './edit-student-feedback-modal.js';

const col = airColumnHelper<StudentFeedback>(getStudentFeedbacksSortableColumnSchema);

export type StudentFeedbacksTableCompatibleRouteId = RoutesWithValidConstraint<StudentFeedbacksTableRouteConstraint>;
export interface StudentFeedbacksTableProps {
  route: RouteById<RegisteredRouteTree, StudentFeedbacksTableCompatibleRouteId>;
  studentId?: string;
}

export function StudentFeedbacksTable({ route, studentId }: StudentFeedbacksTableProps) {
  const { timeZone } = useTimeZone();
  const [sortBy, didChangeSortBy] = useNavigateSearchState(route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentFeedbacksSortableColumn>([sortBy, didChangeSortBy]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      studentFeedbackIssues: [
        [selectedStudentFeedbackIssues, activeSelectedStudentFeedbackIssues],
        setSelectedStudentFeedbackIssues,
      ],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
    },
    resetFilters,
  } = usePaginatedFilters(route.id, ['studentFeedbackIssues', 'statuses', 'startDate', 'endDate']);

  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudentFeedbacks({
      pageIndex,
      pageSize,
      filters: {
        studentId,
        studentFeedbackIssues: activeSelectedStudentFeedbackIssues,
        statuses: activeSelectedStatuses,
        startDate: (() => {
          const startPlanDate = plainDateFromStringOrElseUndefined(selectedStartDate);
          return startPlanDate
            ? startPlanDate.toZonedDateTime(FrequentTimeZones.UsCentral).startOfDay().toInstant().toString()
            : undefined;
        })(),
        endDate: (() => {
          const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);
          return endPlanDate
            ? endPlanDate
                .toZonedDateTime(FrequentTimeZones.UsCentral)
                .add({ days: 1 })
                .startOfDay()
                .subtract({ milliseconds: 1 })
                .toInstant()
                .toString()
            : undefined;
        })(),
      },
      sortBy,
    }),
  );

  const studentFeedbacks = useMemo(() => response?.studentFeedbacks || EmptyArray, [response]);
  useErrorToast(error, 'Error loading review notes. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentFeedback>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => studentFeedbacks.find((studentFeedback) => studentFeedback.id === id),
    });

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const handleEditSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const columns = useMemo(
    () => [
      createTableRowSelectionCell(col),
      col.accessor((row) => row, {
        id: 'edit',
        header: '',
        size: 50,
        cell: ({ getValue }) => {
          const studentFeedback = getValue();
          return (
            <EditStudentFeedbackModal
              studentFeedback={studentFeedback}
              trigger={<PencilIcon size={16} className="cursor-pointer" />}
              onSuccess={handleEditSuccess}
            />
          );
        },
      }),
      col.accessor('createdAtInstant', {
        id: 'createdAtInstant',
        header: 'Date',
        cell: ({ getValue }) => {
          return formatInstant(getValue(), timeZone).formattedDate;
        },
      }),
      col.accessor((row) => row, {
        id: 'reviewer',
        header: 'Reviewer',
        cell: ({ getValue }) => {
          const { reviewer } = getValue();
          return <PeopleBadgeList people={[reviewer]} />;
        },
      }),
      col.accessor('studentFeedbackIssue', {
        id: 'studentFeedbackIssue',
        header: 'Review type',
        cell: ({ getValue }) => StudentFeedbackIssueLabelMap[getValue()],
      }),
      col.accessor('note', {
        id: 'note',
        header: 'Note',
      }),
      col.accessor('status', {
        id: 'status',
        header: 'Issue status',
        cell: ({ getValue }) => StudentFeedbackStatusLabelMap[getValue()],
      }),
    ],
    [handleEditSuccess, timeZone],
  );

  const table = useReactTable({
    data: studentFeedbacks,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterDropdown
          title="Review type"
          options={getStudentFeedbackIssueOptions()}
          selectedValues={selectedStudentFeedbackIssues ?? []}
          onChange={setSelectedStudentFeedbackIssues}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date range"
        />
        <TableFilterDropdown
          title="Issue status"
          options={getStudentFeedbackStatusOptions()}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      selectedStudentFeedbackIssues,
      setSelectedStudentFeedbackIssues,
      selectedStatuses,
      setSelectedStatuses,
      selectedStartDate,
      selectedEndDate,
      setSelectedStartDate,
      setSelectedEndDate,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <StudentFeedbackBulkUpdate
        confirmMessage="Are you sure you want to change the status to Need review for"
        options={{ studentId, status: StudentFeedbackStatus.need_review }}
        onSuccess={onSuccess}
        studentFeedbacks={rowDataSelection}
        title="Need review"
      />
      <StudentFeedbackBulkUpdate
        confirmMessage="Are you sure you want to change the status to Check again for"
        options={{ studentId, status: StudentFeedbackStatus.check_again }}
        onSuccess={onSuccess}
        studentFeedbacks={rowDataSelection}
        title="Check again"
      />
      <StudentFeedbackBulkUpdate
        confirmMessage="Are you sure you want to change the status to Resolved for"
        options={{ studentId, status: StudentFeedbackStatus.resolved }}
        onSuccess={onSuccess}
        studentFeedbacks={rowDataSelection}
        title="Resolved"
      />
    </>
  );

  return (
    <EntityTable
      table={table}
      showPagination
      isLoading={isPending}
      hasError={!!error}
      emptyState={'No students found'}
      errorState={<TableErrorState refetchAction={refetch} />}
      filters={{ filterLeftComponents }}
      bulkActionsMenuItems={bulkActionsMenuItems}
      emptyStateClassName="translate-y-0"
      autoManagedColumnIds={['selection', 'edit']}
    />
  );
}
